/**
 * E-signature API client — contract-SIGN FE-slice (Phase-1, Javo-ratify 2026-05-31).
 *
 * Backend: `@kgc/esign` (4 endpoint, HMAC hash-chain, Prisma-perzisztált) — VERIFIKÁLT
 * (POST /esign/session · POST /esign/session/:id/sign · GET /esign/verify/:documentId ·
 *  GET /esign/session/:id/audit). A pult-mediált flow FE-fele:
 *   ID-beazonosítás → szerződés-PDF generálás → **tablet canvas-aláírás (SignaturePad)** → submit → verify.
 *
 * NB: a signed-PDF *archive* (Story 100-9) Phase-2-re halasztva (Javo-ratify) — itt a
 * session + sign + audit elég az UAT-hoz (`submitSignature` BE jelenleg üres PDF-byte-ot ad).
 */
import { api } from './client';

export type SigningSourceType = 'RENTAL_CONTRACT' | 'RENTAL_RETURN' | 'WORKSHEET' | 'DELIVERY_NOTE';
export type SignatureLevel = 'SES' | 'AES' | 'QES';
export type SignatureFormat = 'svg' | 'png' | 'json';

export interface CreateSigningSessionInput {
  /** A forrás-dokumentum típusa (pl. bérleti szerződés). */
  sourceType: SigningSourceType;
  /** A forrás-entitás UUID-ja (pl. a RentalContract id-ja). */
  sourceId: string;
  /** Aláírás-szint — default `SES` (pult-tablet egyszerű elektronikus aláírás). */
  level?: SignatureLevel;
  /** SES-provider — default `canvas-local`. */
  provider?: string;
  documentId?: string;
  /** Session-lejárat percben (BE default 60, max 10080). */
  expiresInMinutes?: number;
}

export interface SigningSession {
  id: string;
  status: string;
  /** A canvas-aláíró route a BE szerint (`/esign/canvas/:id`). */
  signingUrl: string;
  expiresAt?: string | null;
  level?: string;
  provider?: string;
  createdAt?: string;
}

/**
 * A `/esign/session/:id/sign` válasz-alakja (live dev4 measured-contract, 2026-05-31):
 * `{ data: { sessionId, signedAt, status } }` — NEM teljes SigningSession.
 */
export interface SubmitSignatureResult {
  sessionId: string;
  signedAt: string;
  status: string;
}

export interface SignatureConsent {
  /** Alap-nyilatkozat (kötelező `true`, különben a BE `ESignConsentNotGivenError`-t dob). */
  basicAcknowledged: boolean;
  /** Adatkezelési nyilatkozat (kötelező `true`). */
  dataProcessingAcknowledged: boolean;
}

export interface SubmitSignatureInput {
  /** Canvas-formátum. A SignaturePad `png` dataURL-t ad; `svg`/`json` is támogatott BE-n. */
  format: SignatureFormat;
  /** A canvas-payload string (dataURL/SVG), UTF-8 ≤ 512 KiB. */
  payload: string;
  consent: SignatureConsent;
  /** Kliens-oldali audit-jelek (a BE IP/UA-t maga is rögzíti). */
  clientMetadata?: { strokeCount?: number; durationMs?: number };
}

export interface VerifySignatureResult {
  valid: boolean;
}

/** A backend `{ data: ... }` envelope-ba csomagolja a válaszokat (app-konvenció). */
interface Wrapped<T> {
  data: T;
}

/**
 * A `/esign/session` válasz EXTRA `session`-szinttel ágyaz: `{ data: { session, warning? } }`
 * (live dev4 measured-contract, 2026-05-31 — a korábbi `{ data: SigningSession }` feltevés HIBÁS volt,
 * a session-id undefined lett volna; a live-smoke fogta meg, ld. [[live-smoke-write-only-blindspot]]).
 */
interface SessionEnvelope {
  data: {
    session: SigningSession;
    warning?: string;
  };
}

/** POST /esign/session — aláírási session nyitása egy dokumentumhoz. */
export async function createSigningSession(
  input: CreateSigningSessionInput
): Promise<SigningSession> {
  const body = { level: 'SES' as SignatureLevel, provider: 'canvas-local', ...input };
  const res = await api.post<SessionEnvelope>('/esign/session', body);
  return res.data.session;
}

/** POST /esign/session/:id/sign — a canvas-payload + consent beküldése. */
export async function submitSignature(
  sessionId: string,
  input: SubmitSignatureInput
): Promise<SubmitSignatureResult> {
  const res = await api.post<Wrapped<SubmitSignatureResult>>(`/esign/session/${sessionId}/sign`, {
    sessionId,
    ...input,
  });
  return res.data;
}

/** GET /esign/verify/:documentId — hash-chain integritás-verifikáció. */
export async function verifySignature(documentId: string): Promise<VerifySignatureResult> {
  const res = await api.get<Wrapped<VerifySignatureResult>>(`/esign/verify/${documentId}`);
  return res.data;
}

/** GET /esign/session/:id/audit — audit-evidence JSON (BOLTVEZETO+ jogosultság). */
export async function getSigningAudit(sessionId: string): Promise<Record<string, unknown>> {
  const res = await api.get<Wrapped<Record<string, unknown>>>(`/esign/session/${sessionId}/audit`);
  return res.data;
}
